import { Lock, Printer, ConciergeBell, HandCoins, Undo2 } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "@/components/ui/button";

const STEPS = [
  { icon: ConciergeBell, text: "Ask us for a lock at check-in" },
  { icon: HandCoins, text: "Leave a small refundable holding deposit" },
  { icon: Undo2, text: "Return the lock at checkout — deposit back on the spot" },
];

export default function DeskCard() {
  const guestUrl = `${window.location.origin}/guest`;

  return (
    <div
      data-testid="desk-card-page"
      className="flex min-h-screen flex-col items-center bg-slate-100 px-6 py-12 print:bg-white print:p-0"
    >
      <div className="mb-8 flex flex-col items-center gap-3 print:hidden">
        <Button
          data-testid="print-card-btn"
          onClick={() => window.print()}
          className="group rounded-full bg-accent px-8 font-semibold text-accent-foreground hover:bg-blue-500 transition-colors"
        >
          <Printer className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5" />
          Print card
        </Button>
        <p className="text-xs text-muted-foreground">
          One card per sheet — place it at the front desk next to the lock log.
        </p>
      </div>

      <div
        data-testid="desk-card"
        className="w-full max-w-2xl rounded-lg border border-border bg-white p-10 shadow-sm print:max-w-none print:rounded-none print:border-2 print:border-slate-900 print:shadow-none"
      >
        <div className="flex items-center justify-between border-b border-border pb-6">
          <div className="flex items-center gap-2.5">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary">
              <Lock className="h-4 w-4 text-white" />
            </span>
            <p className="font-serif text-xl font-semibold tracking-tight text-primary">
              SecureStay <span className="italic text-accent">Locks</span>
            </p>
          </div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground">
            Guest amenity
          </p>
        </div>

        <h1 className="mt-8 font-serif text-3xl md:text-4xl tracking-tight text-primary leading-tight">
          Driving? Borrow a steering wheel lock —{" "}
          <span className="italic text-accent">free with your stay.</span>
        </h1>
        <p className="mt-4 text-sm md:text-base leading-relaxed text-muted-foreground">
          A high-visibility lock for your steering wheel while your car is in
          our car park. An added deterrent, and a little extra peace of mind.
        </p>

        <div className="mt-8 space-y-4">
          {STEPS.map((step, i) => (
            <div key={i} data-testid={`desk-card-step-${i + 1}`} className="flex items-center gap-4">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-accent">
                <step.icon className="h-4 w-4" />
              </span>
              <p className="text-sm md:text-base text-foreground">{step.text}</p>
            </div>
          ))}
        </div>

        <div className="mt-10 flex items-center gap-6 rounded-lg border border-dashed border-border bg-slate-50 p-6 print:bg-white">
          <div data-testid="desk-card-qr" className="rounded-lg border border-border bg-white p-3">
            <QRCodeSVG value={guestUrl} size={140} level="M" />
          </div>
          <div>
            <p className="font-serif text-xl tracking-tight text-primary">
              Scan to see how it works
            </p>
            <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
              Two minutes, four steps, everything explained — including how the
              refundable holding deposit works.
            </p>
            <p className="mt-3 break-all text-[11px] font-semibold text-accent">{guestUrl}</p>
          </div>
        </div>

        <p className="mt-8 border-t border-border pt-5 text-[10px] leading-relaxed text-muted-foreground">
          Provided as-is as a guest amenity. A visible lock deters opportunistic
          drive-off and relay theft; no single device stops a determined thief.
        </p>
      </div>
    </div>
  );
}
